import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaChevronDown } from 'react-icons/fa';

import SocialMediaArrow from '../assets/images/social-media-arrow.svg';

import LinkedInIcon from '../assets/icons/linkedin.png';
import GithubIcon from '../assets/icons/github.png';
import FacebookIcon from '../assets/icons/facebook.png';
import InstagramIcon from '../assets/icons/instagram.png';

const SiteFixedUIStyles = styled.div`
  .site__social,
  .site__scrollDown {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    position: fixed;
    bottom: 20px;
    width: 50px;
    z-index: 50;
  }
  .site__social {
    left: 50px;
    bottom: 50px;
  }
  .site__scrollDown {
    right: 50px;
    bottom: 60px;
    cursor: pointer;
  }

  .site__social__indicator {
    width: 50px;
    p {
      font-size: 1.6rem;
      transform: translateY(-70px) rotate(90deg);
      letter-spacing: 0.7rem;
      text-transform: uppercase;
      color: var(--highlight-blue);
    }
  }
  .arrow-icon {
    display: block;
    margin: 0 auto;
    background-color: var(--highlight-blue);
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-position: center;
    mask-position: center;
    transition: background-color 0.3s ease;
  }
  .arrow-icon--social {
    width: 16px;
    height: 45px;
    -webkit-mask-image: url(${SocialMediaArrow});
    mask-image: url(${SocialMediaArrow});
  }
  .scroll-ring-wrap {
    position: relative;
    width: 48px;
    height: 48px;
    svg {
      display: block;
    }
    .scroll-chevron {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: var(--highlight-blue);
      font-size: 1.4rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.4s ease;
      &.flipped {
        transform: translate(-50%, -50%) rotate(180deg);
      }
    }
  }

  .social-icon {
    display: block;
    width: 32px;
    height: 32px;
    margin: 0 auto;
    background-color: var(--highlight-blue);
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-position: center;
    mask-position: center;
    transition: background-color 0.3s ease, opacity 0.2s ease;
    &:hover {
      opacity: 0.7;
    }
  }

  .site__social__text {
    ul {
      li {
        margin-bottom: 1rem;
      }
    }
  }

  /* Tablet — shrink, hide text labels */
  @media only screen and (max-width: 992px) {
    .site__social {
      left: 12px;
      width: 28px;
      gap: 1.2rem;
    }
    .site__scrollDown {
      right: 12px;
      width: 28px;
      gap: 1rem;
    }
    .site__social__indicator p {
      display: none;
    }
    .social-icon {
      width: 24px;
      height: 24px;
    }
    .arrow-icon--social {
      display: none;
    }
    .scroll-ring-wrap {
      width: 36px;
      height: 36px;
      svg {
        width: 36px;
        height: 36px;
      }
      .scroll-chevron {
        font-size: 1.1rem;
      }
    }
  }

  /* Mobile — hide entirely, footer handles social links */
  @media only screen and (max-width: 768px) {
    .site__social,
    .site__scrollDown {
      display: none;
    }
  }
`;

const socialMediaLinks = [
  { href: 'https://www.linkedin.com/in/anasqazi', label: 'LinkedIn', imgLogo: LinkedInIcon },
  { href: 'https://www.facebook.com/ianasqazi',   label: 'Facebook', imgLogo: FacebookIcon },
  { href: 'https://www.instagram.com/ianasqazi/', label: 'Instagram', imgLogo: InstagramIcon },
  { href: 'https://github.com/ianasqazi',         label: 'GitHub', imgLogo: GithubIcon },
];

export default function SiteFixedUI() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const atBottom = scrollProgress > 0.8;

  const RADIUS = 20;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? scrolled / total : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollClick = () => {
    if (atBottom) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
    }
  };

  return (
    <SiteFixedUIStyles>
      {/* Social Icons — Left */}
      <div className="site__social">
        <div className="site__social__indicator">
          <p>Follow</p>
          <div className="arrow-icon arrow-icon--social" />
        </div>
        <div className="site__social__text">
          <ul>
            {socialMediaLinks.map(({ href, label, imgLogo }) => (
              <li key={label}>
                <a href={href} target="_blank" rel="noreferrer" aria-label={label}>
                  <div
                    className="social-icon"
                    style={{
                      WebkitMaskImage: `url(${imgLogo})`,
                      maskImage: `url(${imgLogo})`,
                    }}
                  />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Scroll Indicator — Right */}
      <div
        className="site__scrollDown"
        onClick={handleScrollClick}
        role="button"
        tabIndex={0}
        aria-label={atBottom ? 'Scroll to top' : 'Scroll down'}
        onKeyDown={(e) => e.key === 'Enter' && handleScrollClick()}
      >
        <div className="scroll-ring-wrap">
          <svg viewBox="0 0 48 48" width="48" height="48">
            <circle cx="24" cy="24" r={RADIUS} fill="none" stroke="var(--gray-2)" strokeWidth="2" />
            <circle
              cx="24" cy="24" r={RADIUS}
              fill="none"
              stroke="var(--highlight-blue)"
              strokeWidth="2"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={CIRCUMFERENCE * (1 - scrollProgress)}
              transform="rotate(-90 24 24)"
              style={{ transition: 'stroke-dashoffset 0.15s ease' }}
            />
          </svg>
          <span className={`scroll-chevron${atBottom ? ' flipped' : ''}`}>
            <FaChevronDown />
          </span>
        </div>
      </div>
    </SiteFixedUIStyles>
  );
}
